import { createContext, useContext } from 'react';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { Keyword } from 'src/modules/keywords/types';
import { Group } from 'src/modules/groups/types';
import { CampaignConfigSchema } from '../types';

/**
 * Resolved once per builder open, shared with the fields below. Plain context
 * rather than redux: only one builder is mounted at a time and nothing outside
 * its subtree reads any of it.
 */
export interface BuilderContextValue {
  /** The config driving this builder. Never null below the loading gate. */
  config: CampaignConfigSchema;
  /** `null` means not resolved yet — editors mounted before that strip merge tags. */
  metadata: DefaultCustomField[] | null;
  /** Active company templates, resolved before any editor mounts. */
  messageTemplates?: MessageTemplate[];
  /** Empty unless the config exposes the keywords field. */
  keywords: Keyword[];
  /** Adds an inline-created keyword so picker and preview resolve it without a refetch. */
  addKeyword: (keyword: Keyword) => void;
  /** Backend says the campaign can no longer be edited: inputs disabled, save hidden. */
  isCampaignReadOnly: boolean;
  /** Present only when editing a saved campaign (never for new/duplicate). */
  campaignId?: string;
  /** Duplicate flows create a campaign, but must not run new-campaign auto defaults. */
  isDuplicate?: boolean;
  /** Saved selections may be outside the first page returned by GET /groups. */
  campaignGroups?: Group[];
}

const BuilderContext = createContext<BuilderContextValue | null>(null);

export const BuilderProvider = BuilderContext.Provider;

export const useBuilderContext = (): BuilderContextValue => {
  const value = useContext(BuilderContext);
  if (!value) throw new Error('useBuilderContext must be used inside a BuilderProvider');
  return value;
};

export const useBuilderConfig = (): CampaignConfigSchema => useBuilderContext().config;
export const useBuilderMetadata = (): DefaultCustomField[] | null => useBuilderContext().metadata;
export const useBuilderMessageTemplates = (): MessageTemplate[] =>
  useBuilderContext().messageTemplates ?? [];
export const useBuilderKeywords = (): Keyword[] => useBuilderContext().keywords;

/** Shorthand for the common `disabled={isCampaignReadOnly}` wiring. */
export const useIsReadOnly = (): boolean => useBuilderContext().isCampaignReadOnly;

/**
 * Per-message `isEditable` only means something once the campaign exists, so
 * drafts seeded from a duplicate or preset stay editable despite the frozen flag.
 */
export function computeMessageReadOnly(params: {
  isCampaignReadOnly: boolean;
  campaignId?: string;
  isEditable?: boolean;
}): boolean {
  const { isCampaignReadOnly, campaignId, isEditable } = params;
  return isCampaignReadOnly || (!!campaignId && isEditable === false);
}

export { BuilderContext };
